import { useMemo } from 'react';
import {
  Box, Stack, Typography, Chip, Button, Accordion, AccordionSummary, AccordionDetails, Table, TableHead, TableRow, TableCell, TableBody, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  stNameFromGroups, groupLabelFromApiGroup, ZONES,
  computeDeviations, validateAll, DEVIATION_COLORS, filterPricedCodes, findStGroup,
} from '../constants';
import { useGetServiceabilityGroupsQuery } from '../../../../redux/commonApiCalls';

function downloadCsv(name, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function MetricCard({ label, value, sub, color = 'text.primary' }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 160,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontSize: 22, fontWeight: 800, mt: 0.25, color, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>{sub}</Typography>}
    </Box>
  );
}

function format(v) {
  if (v === null || v === undefined || v === '') return '–';
  return `${v} ${Number(v) === 1 ? 'day' : 'days'}`;
}

function rowColor(v, std) {
  if (v === null || v === undefined || v === '') return null;
  if (Number(v) === 0) return { bg: '#fff7ed', fg: '#9a3412' };
  if (std == null) return null;
  if (Number(v) > std) return DEVIATION_COLORS.higher;
  if (Number(v) < std) return DEVIATION_COLORS.lower;
  return null;
}

function StReviewBlock({ st, groups, standardTat }) {
  const apiGroup = findStGroup(groups, st);
  const stName = stNameFromGroups(st, groups);
  const stGroup = groupLabelFromApiGroup(apiGroup);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const linkedCodes = filterPricedCodes((apiGroup?.linkedCodes || []).filter((lc) => lc.isActive !== false), pricedKeys);
  const parentKeys = [...new Set(linkedCodes.map((lc) => lc.parent || lc.cpAccountCode))];
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const mode = useSelector((s) => s.tatWizard.modeByST[st]) || 'blended';
  const std = standardTat?.[st] || null;

  const rows = mode === 'blended'
    ? [{ label: 'Blended (all CPs)', data: tatBlended[st] || {} }]
    : parentKeys.map((parent) => ({ label: parent, data: tat[st]?.[parent] || {} }));

  return (
    <Accordion defaultExpanded sx={{ '&:before': { display: 'none' }, border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{stName}</Typography>
          <Chip size="small" label={stGroup} sx={{ height: 18, fontSize: 10 }} />
          <Chip
            size="small" label={st}
            sx={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 10, height: 18, bgcolor: 'grey.100' }}
          />
          <Chip
            size="small"
            label={mode === 'blended' ? 'Blended' : 'Courier-wise'}
            sx={{
              height: 18, fontSize: 10, fontWeight: 700,
              bgcolor: mode === 'blended' ? '#fff7ed' : '#eef2ff',
              color: mode === 'blended' ? '#9a3412' : '#3730a3',
            }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1100, '& th, & td': { fontSize: 11.5, px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2} sx={{ minWidth: 200, bgcolor: 'grey.50', fontWeight: 700 }}>
                  {mode === 'blended' ? 'Scope' : 'Parent'}
                </TableCell>
                {ZONES.map((z) => (
                  <TableCell key={z.id} colSpan={3} align="center" sx={{ bgcolor: 'grey.50', fontWeight: 700, borderLeft: 1, borderColor: 'divider' }}>
                    {z.name}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                {ZONES.map((z) => ([
                  <TableCell key={`${z.id}-min`} align="center" sx={{ bgcolor: 'grey.50', fontSize: 10, borderLeft: 1, borderColor: 'divider' }}>Min</TableCell>,
                  <TableCell key={`${z.id}-max`} align="center" sx={{ bgcolor: 'grey.50', fontSize: 10 }}>Max</TableCell>,
                  <TableCell key={`${z.id}-std`} align="center" sx={{ bgcolor: 'grey.50', fontSize: 10, color: 'text.secondary' }}>Std</TableCell>,
                ]))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.label}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.label}</TableCell>
                  {ZONES.map((z) => {
                    const stdMin = std?.[z.id]?.[0];
                    const stdMax = std?.[z.id]?.[1];
                    const mn = r.data[`z${z.id}_min`];
                    const mx = r.data[`z${z.id}_max`];
                    const cMin = rowColor(mn, stdMin);
                    const cMax = rowColor(mx, stdMax);
                    return ([
                      <TableCell key={`${z.id}-min`} align="center" sx={{ borderLeft: 1, borderColor: 'divider', bgcolor: cMin?.bg, color: cMin?.fg, fontWeight: cMin ? 700 : 400 }}>{format(mn)}</TableCell>,
                      <TableCell key={`${z.id}-max`} align="center" sx={{ bgcolor: cMax?.bg, color: cMax?.fg, fontWeight: cMax ? 700 : 400 }}>{format(mx)}</TableCell>,
                      <TableCell key={`${z.id}-std`} align="center" sx={{ color: 'text.secondary', fontSize: 10.5 }}>
                        {stdMin != null && stdMax != null ? `${stdMin}–${stdMax}d` : '–'}
                      </TableCell>,
                    ]);
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function Stage3Review() {
  const { enqueueSnackbar } = useSnackbar();
  const tat = useSelector((s) => s.tatWizard.tat);
  const tatBlended = useSelector((s) => s.tatWizard.tatBlended);
  const modeByST = useSelector((s) => s.tatWizard.modeByST);
  const merchantName = useSelector((s) => s.tatWizard.merchantName);
  const selectedSTs = useSelector((s) => s.tatWizard.selectedSTs);
  const standardTat = useSelector((s) => s.tatWizard.standardTat);
  const pricedKeys = useSelector((s) => s.tatWizard.pricedKeys || []);
  const { data: groups = [] } = useGetServiceabilityGroupsQuery();

  // Only service types resolved by the Operating Profile AND with couriers mapped
  // produce reviewable rows / CSVs.
  const reviewSts = useMemo(
    () => (selectedSTs || []).filter((st) => (findStGroup(groups, st)?.linkedCodes?.filter((lc) => lc.isActive !== false)?.length || 0) > 0),
    [selectedSTs, groups],
  );

  const summary = useMemo(
    () => computeDeviations(tat, tatBlended, modeByST, selectedSTs, standardTat, groups),
    [tat, tatBlended, modeByST, selectedSTs, standardTat, groups],
  );
  const issues = useMemo(
    () => validateAll(tat, tatBlended, modeByST, selectedSTs, groups, pricedKeys),
    [tat, tatBlended, modeByST, selectedSTs, groups, pricedKeys],
  );

  const pct = (n) => (summary.total ? `${((n / summary.total) * 100).toFixed(1)}%` : '0.0%');

  const onExportDev = () => {
    if (!summary.detail.length) {
      enqueueSnackbar('No deviations from standard TAT to export', { variant: 'info' });
      return;
    }
    const cols = ['service_type', 'parent', 'account_code', 'zone', 'bound', 'entered_days', 'standard_days', 'delta_days', 'direction'];
    const rows = summary.detail.map((d) => [
      d.service_type, d.parent, d.account_code, d.zone, d.bound, d.entered, d.standard, d.delta, d.direction,
    ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`TAT_deviations_${merchantName}_${stamp}.csv`, [cols.join(','), ...rows].join('\n'));
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5}>
        <MetricCard label="Discrepancies vs Standard" value={summary.deviations} sub={pct(summary.deviations)} />
        <MetricCard label="Higher than standard" value={summary.higher} sub={pct(summary.higher)} color="error.main" />
        <MetricCard label="Lower than standard" value={summary.lower} sub={pct(summary.lower)} color="success.main" />
        <MetricCard label="Zero values" value={summary.zeros} sub={pct(summary.zeros)} color="warning.main" />
        <MetricCard label="Empty / missing" value={summary.empty} sub={pct(summary.empty)} color="warning.main" />
      </Stack>

      {issues.length === 0 ? (
        <Alert
          icon={<CheckCircleOutlineIcon />}
          severity="success"
          sx={{ '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>All TATs validate cleanly.</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
            No empty cells, negative values, or Min &gt; Max violations. You may proceed to submit.
          </Typography>
        </Alert>
      ) : (
        <Alert
          icon={<ReportProblemOutlinedIcon />}
          severity="error"
          sx={{ '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {issues.length} issue(s) found — submit blocked
          </Typography>
          <Stack component="ul" spacing={0.25} sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            {issues.slice(0, 10).map((iss) => (
              <Typography component="li" key={iss} variant="caption" sx={{ display: 'list-item' }}>
                {iss}
              </Typography>
            ))}
            {issues.length > 10 && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
                …and {issues.length - 10} more.
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Box>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          onClick={onExportDev}
          sx={{ fontSize: 12 }}
        >
          Export deviations CSV
        </Button>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Per-service-type review
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
            {reviewSts.length} service type(s) in scope from the Operating Profile
          </Typography>
        </Typography>
        {reviewSts.map((st) => <StReviewBlock key={st} st={st} groups={groups} standardTat={standardTat} />)}
      </Box>
    </Stack>
  );
}
